import React, { useEffect, useReducer, useState } from "react";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Modal,
  OutlinedInput,
  Select,
  styled,
  TablePagination,
  TextField,
  Button
} from "@mui/material";
import TableHeader from "../table/TableHeader";
import useDebounce from "../../hooks/useDebounce";
import MainTableBody from "../table/MainTableBody";
import CustomTable from "../table/CustomTable";
import { LoadingTableSkeleton } from "../../utils/common";
import NoDataTable from "../table/NoDataTable";
import { Add, CloseOutlined } from "@mui/icons-material";
import UserTableRow from "./UserTableRow";
import { CustomizeTextField } from "../templates/TemplateTable";
import useAuthFetch from "../../hooks/useAuthFetch";
import { useLocation, useNavigate } from "react-router-dom";
import ConfirmBox from "../ConfirmBox";
import toast from "react-hot-toast";
import FilterDropdown from "../filter/FilterDropdown";

const actions = Object.freeze({
  pageChange: "PAGE_CHANGE",
  perPageChange: "PER_PAGE_CHANGE",
  queryChange: "QUERY_CHANGE",
  filterChange: "FILTER_CHANGE",
});

export const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 600,
  bgcolor: "background.paper",
  boxShadow: 24,
  paddingLeft: 10,
  paddingRight: 10,
  paddingTop: 8,
  paddingBottom: 8,
};


const CustomSelect = styled(Select)`
  & .MuiOutlinedInput-notchedOutline {
    border: none;
  }
`;

export const CreateUserModal = ({ refetch, setOpenErr, setErrMsg }) => {
  const [userName, setUserName] = React.useState("");
  const [ntId, setNtId] = React.useState("");
  const [role, setRole] = React.useState("");
  const [meTicketNo, setMeTicketNo] = React.useState("");
  const [open, setOpen] = React.useState(false);
  const [validationErr, setValidationErr] = useState({
    userName: false,
    ntId: false,
    role: false,
    meTicketNo: false,
  });

  const customFetch = useAuthFetch();

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setUserName("")
    setNtId("")
    setRole("")
    setMeTicketNo("")
    setValidationErr({
      userName: false,
      ntId: false,
      role: false,
      meTicketNo: false,
    })
    setOpen(false);
  }


  const validateFields = () => {
    const errors = {
      userName: !/^[A-Za-z_]{1,15}$/.test(userName),
      ntId: !/^[A-Za-z0-9]{1,10}$/.test(ntId),
      role: role === "",
      meTicketNo: !/^[A-Za-z0-9]{1,15}$/.test(meTicketNo),
    };

    setValidationErr(errors);
    return !Object.values(errors).some((err) => err);
  };

  const handleSubmit = async () => {
    if (!validateFields()) return;

    const url = new URL(`${process.env.REACT_APP_API_URL}/v1/dashboard/user/create`);
    const bodyContent = { userName, ntID: ntId, role, meTicketNo }
    const method = "POST"
    const { response, status, message } = await customFetch(
      url,
      method,
      bodyContent
    );
    if (status === 201) {
      toast.success("User Created Successfully!", { position: "bottom-right" });
      refetch();
    } else {
      setOpenErr(true);
      setErrMsg(message || "Something went wrong while creating the user!");
    }
    handleClose();
  };

  return (
    <div>
      <button
        onClick={handleOpen}
        className="bg-purple-300 flex items-center py-2 pl-2 pr-3 text-white font-bold rounded"
      >
        <Add fontSize="small" className="ml-0 pl-0" />
        Create
      </button>

      <Modal open={open} onClose={handleClose} aria-labelledby="modal-title">
        <Box sx={style} className="rounded-xl relative">
          <button className="absolute top-2 right-3" onClick={handleClose}>
            <CloseOutlined className="w-5 h-5 text-gray-450 hover:text-black" />
          </button>

          <p className="text-2xl font-extrabold text-center text-gray-850">Create New User</p>

          {/** Input Fields **/}
          {[
            { label: "Enter User Name", value: userName, setter: setUserName, error: validationErr.userName, errorMsg: "Username must be alphabets or '_' (1-15 chars)." },
            { label: "Enter ntId", value: ntId, setter: setNtId, error: validationErr.ntId, errorMsg: "NtID must be alphanumeric (1-10 chars)." },
            { label: "Enter IDM Task ID", value: meTicketNo, setter: setMeTicketNo, error: validationErr.meTicketNo, errorMsg: "IDM Task ID must be alphanumeric (1-15 chars)." },
          ].map(({ label, value, setter, error, errorMsg }, index) => (
            <div key={index} className="mt-4">
              <FormControl variant="outlined" className="w-full">
                <CustomizeTextField
                  placeholder={label}
                  className="bg-[#F3F4F8] text-black w-full rounded-lg"
                  value={value}
                  onChange={(e) => setter(e.target.value)}
                />
              </FormControl>
              {error && <p className="text-red-500 text-sm mt-1">{errorMsg}</p>}
            </div>
          ))}

          {/** Role Dropdown **/}
          <div className="mt-4">
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <CustomSelect
                value={role}
                onChange={(e) => setRole(e.target.value)}
                className="bg-[#F3F4F8] text-black font-semibold w-full rounded-lg"
              >
                <MenuItem value="ADMIN">Admin</MenuItem>
                <MenuItem value="USER">User</MenuItem>
                <MenuItem value="GCUSER">GIFT City User</MenuItem>
                <MenuItem value="SUPPORT">IDM Support</MenuItem>
                <MenuItem value="ASSESSOR">Assessor</MenuItem>
              </CustomSelect>
            </FormControl>
            {validationErr.role && <p className="text-red-500 text-sm mt-1">Role is required.</p>}
          </div>

          {/** Submit Button **/}
          <div className="mt-4 flex items-center justify-center">
            <button className="bg-purple-300 py-3 w-full text-white text-base font-bold rounded-lg" onClick={handleSubmit}>
              Create
            </button>
          </div>
        </Box>
      </Modal>
    </div>
  );
};


const EditUserModal = ({ setErrMsg, open, setOpen, row, refetch, setOpenErr }) => {
  const customFetch = useAuthFetch();
  const [userName, setUserName] = React.useState(row.userName);
  const [ntId, setNtId] = React.useState(row.ntID);
  const [role, setRole] = React.useState(row.role);
  const [meTicketNo, setMeTicketNo] = React.useState(row.meTicketNo);
  const [validationErr, setValidationErr] = useState({
    userName: false,
    ntId: false,
    role: false,
    meTicketNo: false,
  });

  const handleSubmit = async () => {
    if (!validateFields()) return;

    const url = new URL(`${process.env.REACT_APP_API_URL}/v1/dashboard/user/update`);
    const method = "POST"
    const bodyContent = { id: row?.userId, userName, ntID: ntId, role, meTicketNo }
    const { response, status, message } = await customFetch(
      url,
      method,
      bodyContent
    );

    if (status === 200) {
      toast.success(message, {
        position: "bottom-right",
      })
      refetch()
    }
    else {
      setOpenErr(true)
      setErrMsg(message ? message : "Something went wrong while updating user details!")
    }
    handleClose();
  }

  const validateFields = () => {
    const errors = {
      userName: !/^[A-Za-z_]{1,15}$/.test(userName),
      ntId: !/^[A-Za-z0-9]{1,10}$/.test(ntId),
      role: role === "",
      meTicketNo: !/^[A-Za-z0-9]{1,15}$/.test(meTicketNo),
    };

    setValidationErr(errors);
    return !Object.values(errors).some((err) => err);
  };

  const handleClose = () => {
    setUserName("")
    setNtId("")
    setRole("")
    setMeTicketNo("")
    setValidationErr({
      userName: false,
      ntId: false,
      role: false,
      meTicketNo: false,
    })
    setOpen(false);
  }

  return (
    <div>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 600,
            bgcolor: "background.paper",
            boxShadow: 24,
            paddingLeft: 8,
            paddingRight: 8,
            paddingTop: 8,
            paddingBottom: 8,
          }}
          className="rounded-xl relative"
        >
          <button className="absolute top-2 right-3" onClick={handleClose}>
            <CloseOutlined className="w-5 h-5  text-gray-450 hover:text-black" />
          </button>
          <p className="text-2xl mb-0 font-extrabold text-center text-gray-850">
            Edit User Details
          </p>
          {[
            { label: "Enter User Name", value: userName, setter: setUserName, error: validationErr.userName, errorMsg: "Username must be alphabets or '_' (1-15 chars)." },
            { label: "Enter ntId", value: ntId, setter: setNtId, error: validationErr.ntId, errorMsg: "NtID must be alphanumeric (1-10 chars)." },
            { label: "Enter IDM Task ID", value: meTicketNo, setter: setMeTicketNo, error: validationErr.meTicketNo, errorMsg: "IDM Task ID must be alphanumeric (1-15 chars)." },
          ].map(({ label, value, setter, error, errorMsg }, index) => (
            <div key={index} className="mt-4">
              <FormControl variant="outlined" className="w-full">
                <CustomizeTextField
                  placeholder={label}
                  className="bg-[#F3F4F8] text-black w-full rounded-lg"
                  value={value}
                  onChange={(e) => setter(e.target.value)}
                />
              </FormControl>
              {error && <p className="text-red-500 text-sm mt-1">{errorMsg}</p>}
            </div>
          ))}

          {/** Role Dropdown **/}
          <div className="mt-4">
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <CustomSelect
                value={role}
                onChange={(e) => setRole(e.target.value)}
                className="bg-[#F3F4F8] text-black font-semibold w-full rounded-lg"
              >
                <MenuItem value="ADMIN">Admin</MenuItem>
                <MenuItem value="USER">User</MenuItem>
                <MenuItem value="GCUSER">GIFT City User</MenuItem>
                <MenuItem value="SUPPORT">IDM Support</MenuItem>
                <MenuItem value="ASSESSOR">Assessor</MenuItem>

              </CustomSelect>
            </FormControl>
            {validationErr.role && <p className="text-red-500 text-sm mt-1">Role is required.</p>}
          </div>
          <div className="mt-7 flex items-center justify-center">
            <button
              className="bg-purple-300 py-3 px-9 text-white text-base font-bold rounded-lg"
              onClick={handleSubmit}
            >
              Update
            </button>
          </div>
        </Box>
      </Modal>
    </div>
  );
};

export default function UserTable({ reqState, object,
  setObject, handleFetch, handleFetchWithPage }) {
  const [rowsPerPage, setRowsPerPage] = useState(0);
  const [page, setPage] = React.useState(0);
  const [open, setOpen] = React.useState(false);
  const [openErr, setOpenErr] = useState(false)
  const [errMsg, setErrMsg] = useState(null)
  const [selectedRow, setSelectedRow] = React.useState("");
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (reqState.response?.pageInfo) {
      setRowsPerPage(reqState.response.pageInfo.perPage);
      setPage(reqState.response.pageInfo.currentPage - 1); // Convert to 0-indexed for MUI
    }
  }, [reqState?.response]);

  const updateQueryParams = (updatedParams) => {
    const searchParams = new URLSearchParams(location.search);

    // Update or remove query parameters based on `updatedParams`
    Object.keys(updatedParams).forEach((key) => {
      if (updatedParams[key] !== undefined && updatedParams[key] !== null) {
        searchParams.set(key, updatedParams[key]);
      } else {
        searchParams.delete(key);
      }
    });

    // Navigate with updated query parameters
    navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });
  };

  const { isLoading, response } = reqState;

  const refetch = () => {
    // const q = { ...query, page: 1 };
    // if (!q.query) delete q.query;
    // navigate({
    //   pathname: window.location.pathname, // current path
    //   search: `?${new URLSearchParams(q).toString()}`, // query string
    // }, { replace: true });
    handleFetch()
  };

  const columns = ["User Name", "ntID", "Role", "IDM Task Id", "Last Successfull Login", "Last Failed Login", "Actions"];

  useEffect(() => {
    if (response?.pageInfo) {
      setRowsPerPage(response?.pageInfo.perPage)
      setPage(response?.pageInfo?.currentPage - 1)
    }
  }, [response])


  const handleChangePage = (event, newPage) => {
    const updatedPage = newPage + 1; // Material-UI uses 0-based indexing
    updateQueryParams({ page: updatedPage });
    handleFetchWithPage(updatedPage); // Trigger fetch for the new page
  };

  const handleRowsPerPageChange = (event) => {
    const newPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newPerPage);
    setPage(0); // Reset to the first page
    updateQueryParams({ perPage: newPerPage, page: 1 });
  };
  return (
    <>
      <div
        className="py-1 px-4"
        style={{
          height: `calc(100vh - 100px)`,
        }}
      >
        <div className="flex flex-row mb-4 items-center justify-between">
          <p className="text-xl mb-0 font-extrabold">Users ({response?.pageInfo.total})
          </p>
          <div className="flex flex-row">
            <FilterDropdown object={object} setObject={setObject} />

            <button onClick={() => refetch()} className="border focus:border-blue-600 flex items-center border-[#E0E0E0] mx-2 rounded py-1.5 px-3">Refresh Data</button>
            <CreateUserModal setErrMsg={setErrMsg} setOpenErr={setOpenErr} refetch={refetch} />
          </div>

        </div>
        {!response?.data && !isLoading ? (
          <NoDataTable
            columns={columns}
            recordingKeyClassName={true}
            message={"Server Error, Please try again later."}
          />
        ) : response?.data?.length === 0 && !isLoading ? (
          <NoDataTable
            columns={columns}
            recordingKeyClassName={true}
            message={"No users available. Create a user to see it here."}
          />
        ) : (
          <CustomTable>
            <TableHeader columns={columns} />
            <MainTableBody isLoading={isLoading}>
              {response?.data?.length === 0
                ? [1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                  <LoadingTableSkeleton key={i} index={i} />
                ))
                : response?.data?.map((row, i) => (
                  <UserTableRow
                    key={i}
                    row={row}
                    refetch={refetch}
                    setOpen={setOpen}
                    setSelectedRow={setSelectedRow}
                  />
                ))}
            </MainTableBody>

            {response?.pageInfo && (
              <TablePagination
                className="sticky bottom-0 bg-white"
                rowsPerPageOptions={[20]}
                count={response?.pageInfo.total}
                rowsPerPage={rowsPerPage}
                page={page}
                onRowsPerPageChange={handleRowsPerPageChange}
                onPageChange={handleChangePage}
              />
            )}
          </CustomTable>
        )}
      </div>
      {open && (
        <EditUserModal setErrMsg={setErrMsg} setOpenErr={setOpenErr} refetch={refetch} open={open} setOpen={setOpen} row={selectedRow} />
      )}
      <ConfirmBox
        open={openErr}
        setOpen={setOpenErr}
        buttonText={"Close"}
        text={errMsg}
      />
    </>
  );
}
